import React from 'react';

export type SkuSoftBreakPart =
  | { readonly kind: 'text'; readonly value: string }
  | { readonly kind: 'break'; readonly after: '-' | '.' };

/**
 * Soft wrap priority for SKUs:
 * 1. Before `-` (hyphen starts the next line, not trailing the previous)
 * 2. Before `.`
 * 3. Mid-token only when a single segment still overflows (CSS `overflow-wrap`)
 */
export function getSkuSoftBreakParts(sku: string): SkuSoftBreakPart[] {
  if (!sku) {
    return [];
  }

  const parts: SkuSoftBreakPart[] = [];
  const hyphenChunks = sku.split(/(-)/);

  for (const chunk of hyphenChunks) {
    if (chunk === '-') {
      parts.push({ kind: 'break', after: '-' });
      parts.push({ kind: 'text', value: '-' });
      continue;
    }
    if (!chunk) {
      continue;
    }
    if (!chunk.includes('.')) {
      parts.push({ kind: 'text', value: chunk });
      continue;
    }
    const dotChunks = chunk.split(/(\.)/);
    for (const dotChunk of dotChunks) {
      if (dotChunk === '.') {
        parts.push({ kind: 'break', after: '.' });
        parts.push({ kind: 'text', value: '.' });
      } else if (dotChunk) {
        parts.push({ kind: 'text', value: dotChunk });
      }
    }
  }

  return parts;
}

/** Renders a SKU with `<wbr>` before `-` then `.` so wrapped lines do not end on delimiters. */
export function renderSkuWithSoftBreaks(sku: string): React.ReactNode {
  const parts = getSkuSoftBreakParts(sku);
  if (parts.length === 0) {
    return sku;
  }

  return parts.map((part, index) => {
    if (part.kind === 'break') {
      return <wbr key={`sku-wbr-${index}`} />;
    }
    return (
      <React.Fragment key={`sku-text-${index}`}>{part.value}</React.Fragment>
    );
  });
}
